import React, { useEffect, useState } from "react";
import axios from "axios";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { BASE_URL } from "../../config";
import { checkUsernameUniqueness } from "../../actions/index.js";
import Modal, { ModalBody, ModalFooter, ModalHeader } from "../ui/Modal";
import { Checkbox, Field, Input } from "../ui/Field";
import { Button } from "../ui/Button";

const DEFAULT_DATA = {
  first_name: "",
  last_name: "",
  company: "",
  position: "",
  email: "",
  password: "",
  repeatPassword: "",
  username: "",
  user_type: "normal",
};

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function SignUpDialog({
  openDialog,
  handleCloseDialog,
  onSubmit,
  handleClickAlreadyLogin,
}) {
  const [open, setOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [data, setData] = useState(DEFAULT_DATA);
  const [errors, setErrors] = useState({ username: "", password: "" });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => setOpen(openDialog), [openDialog]);

  const close = () => {
    setOpen(false);
    handleCloseDialog(false);
  };

  const change = (key) => async (e) => {
    const value = e.target.value;
    setErrors((prev) => ({ ...prev, password: "" }));
    setData((prev) => ({ ...prev, [key]: value }));

    if (key === "username" && value) {
      const isUnique = await checkUsernameUniqueness(value);
      setErrors((prev) => ({
        ...prev,
        username: isUnique ? "" : "Username already taken",
      }));
    }
  };

  const submit = async () => {
    if (data.password !== data.repeatPassword) {
      setErrors((prev) => ({ ...prev, password: "Passwords do not match." }));
      return;
    }
    if (errors.username) return;

    const postData = { ...data };
    delete postData.repeatPassword;
    try {
      await axios.post(`${BASE_URL}/user/register`, postData);
      onSubmit(data);
    } catch ({ response }) {
      setSnackbar({
        open: true,
        message: response?.data?.message || response?.message || "Error",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Modal open={open} onClose={close} maxWidth="max-w-xl">
        <ModalHeader
          title="Create your BIOS account"
          subtitle="Join researchers, educators, and students bringing biology into the digital age."
          onClose={close}
        />
        <ModalBody className="space-y-4">
          <Field label="Username" error={errors.username}>
            <Input placeholder="username" value={data.username} onChange={change("username")} />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="First name">
              <Input value={data.first_name} onChange={change("first_name")} />
            </Field>
            <Field label="Last name">
              <Input value={data.last_name} onChange={change("last_name")} />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Institution / Company">
              <Input value={data.company} onChange={change("company")} />
            </Field>
            <Field label="Position">
              <Input value={data.position} onChange={change("position")} />
            </Field>
          </div>

          <Field label="Email">
            <Input
              type="email"
              autoComplete="email"
              placeholder="you@lab.edu"
              value={data.email}
              onChange={change("email")}
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Password">
              <Input
                type="password"
                autoComplete="new-password"
                value={data.password}
                onChange={change("password")}
              />
            </Field>
            <Field label="Repeat password" error={errors.password}>
              <Input
                type="password"
                autoComplete="new-password"
                value={data.repeatPassword}
                onChange={change("repeatPassword")}
              />
            </Field>
          </div>

          <Checkbox
            checked={agreed}
            onChange={() => setAgreed((v) => !v)}
            label="I agree to the terms & conditions of BIOS Inc."
          />
        </ModalBody>
        <ModalFooter>
          <Button onClick={submit} disabled={!agreed} className="w-full">
            Create account
          </Button>
          <p className="text-sm text-center text-figures-muted dark:text-brand-mutedInv">
            Already have an account?{" "}
            <button
              onClick={handleClickAlreadyLogin}
              className="accent font-semibold hover:underline"
            >
              Sign in
            </button>
          </p>
        </ModalFooter>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
